import { Check, ChevronDown, ExternalLink } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnrollment } from "@/contexts/EnrollmentContext";

export default function ProgrammeSwitcher() {
  const { enrollment, allEnrollments, switchProgramme } = useEnrollment();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const handleSwitch = async (programmeId: string) => {
    if (isSwitching) return;

    setIsSwitching(true);
    setIsOpen(false);

    try {
      await switchProgramme(programmeId);
    } catch (error) {
      console.error("Failed to switch programme:", error);
      // Could add toast notification here
    } finally {
      setIsSwitching(false);
    }
  };

  // If no enrollment at all, show static title without dropdown
  if (!enrollment) {
    return <h1 className="text-lg font-semibold text-white truncate">Dominion Programme</h1>;
  }

  // Always show dropdown (even with 1 programme) to allow access to "See all programmes"
  return (
    <div className="relative flex-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        className="flex items-center justify-between w-full gap-2 text-left hover:opacity-80 transition-opacity disabled:opacity-50"
        aria-label="Switch programme"
      >
        <div className="min-w-0">
          <span className="block text-[9px] font-bold uppercase tracking-[.18em] leading-[1.4] text-[#F2EFE3]/50">
            Current Programme
          </span>
          <h1 className="mt-0.5 text-[13.5px] font-bold text-white truncate leading-tight">
            {enrollment?.programme?.name || "Select Programme"}
          </h1>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-white/70 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown */}
          <div className="fixed inset-x-3 top-[calc(5.5rem+env(safe-area-inset-top))] z-50 rounded-xl border border-sidebar-border bg-sidebar shadow-xl md:absolute md:inset-x-auto md:left-0 md:top-full md:mt-2 md:w-72">
            <div className="p-2">
              <div className="px-3 py-2 text-[10px] text-sidebar-muted font-bold uppercase tracking-[2px]">
                My Programmes
              </div>
              {allEnrollments.map((enroll) => (
                <button
                  key={enroll.id}
                  onClick={() => handleSwitch(enroll.programme_id)}
                  disabled={isSwitching}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-sidebar-accent transition-colors text-left disabled:opacity-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sidebar-foreground font-medium truncate text-sm">
                      {enroll.programme?.name}
                    </div>
                    <div className="text-sidebar-muted text-xs">
                      Week {enroll.current_week} of {enroll.programme?.total_weeks}
                      {enroll.programme?.status && (
                        <span className="ml-2">• {enroll.programme.status}</span>
                      )}
                    </div>
                  </div>
                  {enroll.is_preferred && (
                    <Check className="w-4 h-4 text-brand flex-shrink-0 ml-2" />
                  )}
                </button>
              ))}

              {/* See All Programmes Link */}
              <div className="mt-2 pt-2 border-t border-sidebar-border">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/my-programmes");
                  }}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-sidebar-accent transition-colors text-left text-brand"
                >
                  <span className="text-sm font-medium">See all programmes</span>
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
